import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, EventEmitter, Input, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToasterService } from '../../../../../core/services/toaster.service';
import { ApiService } from '../../../../../core/services/api.service';
import { DropdownComponent, DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { SlidePanelComponent } from '../../../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../../../shared/components/ui/input/input.component';
import { ToggleComponent } from '../../../../../shared/components/ui/toggle/toggle.component';
import { IconComponent } from '../../../../../shared/icons/icon.component';
import { forEach } from 'lodash-es';

@Component({
  selector: 'app-user-form-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    ButtonComponent,
    SlidePanelComponent,
    InputComponent,
    ToggleComponent,
    IconComponent
  ],
  templateUrl: './user-form-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .custom-scrollbar::-webkit-scrollbar {
        width: 5px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(139, 92, 246, 0.2);
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(139, 92, 246, 0.4);
      }
    `,
  ]
})
export class UserFormPanelComponent implements OnInit {
  panelOpen = signal(false);
  isEditing = signal(false);
  editingId = signal<string | null>(null);
  isRequest = signal(false);

  @Input() from = 'user';
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private toasterService = inject(ToasterService);
  private apiservice = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  isLoading = signal(false);
  formSubmitted = signal(false);

  private readonly noteValidators = [Validators.minLength(10), Validators.pattern('^[a-zA-Z0-9\\s.,]*$')];

  form: FormGroup;
  roleOptions: DropdownOption[] = [];
  formStatusOptions: DropdownOption[] = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'SUSPENDED', label: 'Suspended' },
  ];

  allProducts = signal<any[]>([]);
  productOptions = signal<DropdownOption[]>([]);
  productSearchTerm = signal('');

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern('^[a-zA-Z]*$')]],
      lastName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern('^[a-zA-Z]*$')]],
      phoneNumber: ['', [Validators.minLength(10), Validators.maxLength(15), Validators.pattern('^[0-9]*$')]],
      roleId: ['', [Validators.required]],
      isTwoFAEnabled: [true],
      status: ['ACTIVE', [Validators.required]],
      productAccessType: ['ALL_PRODUCTS', [Validators.required]],
      productIds: [[]],
      note: ['', this.noteValidators],
    });
  }

  ngOnInit() {
    this.fetchRoles();
    this.loadProducts();
  }

  filteredProducts = computed(() => {
    const term = this.productSearchTerm().toLowerCase();
    const products = this.allProducts();
    if (!term) return products;
    return products.filter((p) =>
      (p.name || p.displayName || p.label)?.toLowerCase().includes(term),
    );
  });

  selectedCount = computed(() => {
    const selectedIds = this.form.get('productIds')?.value || [];
    return selectedIds.length;
  });

  isProductSelected(product: any): boolean {
    const selectedIds = this.form.get('productIds')?.value || [];
    return selectedIds.includes(product.id) || (product.pid && selectedIds.includes(product.pid));
  }

  isAllProductsSelected(): boolean {
    const selectedIds = this.form.get('productIds')?.value || [];
    const allIds = this.allProducts().map((p) => p.id);
    return allIds.length > 0 && this.allProducts().every((p) => selectedIds.includes(p.id));
  }

  onProductToggle(product: any): void {
    const control = this.form.get('productIds');
    const selectedIds = [...(control?.value || [])];
    
    const indexId = selectedIds.indexOf(product.id);
    const indexPid = product.pid ? selectedIds.indexOf(product.pid) : -1;

    if (indexId > -1) {
      selectedIds.splice(indexId, 1);
    } else {
      selectedIds.push(product.id);
    }

    control?.setValue(selectedIds);
    this.cdr.markForCheck();
  }

  toggleAllProductsLocal(): void {
    const isAllSelected = this.isAllProductsSelected();
    const control = this.form.get('productIds');

    if (!isAllSelected) {
      control?.setValue(this.allProducts().map((p) => p.id));
    } else {
      control?.setValue([]);
    }
    this.cdr.markForCheck();
  }

  fetchRoles() {
    this.apiservice.get('/roles/list').subscribe({
      next: (res: any) => {
        let rolesData = [];
        if (res?.roles && Array.isArray(res.roles)) {
          rolesData = res.roles;
        } else if (Array.isArray(res)) {
          rolesData = res;
        } else if (res?.data && Array.isArray(res.data)) {
          rolesData = res.data;
        }

        if (rolesData.length > 0) {
          this.roleOptions = rolesData.map((role: any) => ({
            value: String(role.id),
            label: role.name || role.displayName || role.label,
          }));
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        this.toasterService.error('Failed to fetch roles');
        console.error('Role fetch error:', err);
      },
    });
  }

  loadProducts() {
    this.apiservice.fetchProducts().subscribe({
      next: (products) => {
        const uniqueProducts = Array.from(
          new Map(products.map((p) => [p.pid || p.id, p])).values(),
        );
        this.allProducts.set(uniqueProducts);
        this.productOptions.set(
          uniqueProducts.map((p) => ({
            value: p.id,
            label: p.name || p.displayName || p.label,
          })),
        );

        if (
          !this.isEditing() &&
          this.form.get('productAccessType')?.value === 'ALL_PRODUCTS'
        ) {
          this.form.get('productIds')?.setValue(uniqueProducts.map((p) => p.id));
        }
      },
    });
  }

  openPanel(user?: any, prefill?: any, isRequest: boolean = false) {
    this.isRequest.set(isRequest);

    const noteValidators = isRequest ? [Validators.required, ...this.noteValidators] : this.noteValidators;
    this.form.get('note')?.setValidators(noteValidators);
    this.form.get('note')?.updateValueAndValidity();

    // Set common fields to enable initially
    this.form.get('firstName')?.enable();
    this.form.get('lastName')?.enable();
    this.form.get('email')?.enable();
    this.form.get('phoneNumber')?.enable();

    if (user) {
      this.isEditing.set(true);
      this.editingId.set(user.id);

      const productIds =
        user.UserProducts?.map((up: any) => up.productId || up.product?.id) ||
        [];

      this.form.patchValue({
        ...user,
        productIds: productIds,
      });
      this.form.get('email')?.disable();
    } else {
      this.isEditing.set(false);
      this.editingId.set(null);
      
      this.form.reset({
        status: 'ACTIVE',
        isTwoFAEnabled: true,
        productAccessType: 'ALL_PRODUCTS',
        productIds: [],
      });

      if (prefill) {
        this.form.patchValue({
          email: prefill.email || '',
          firstName: prefill.firstName || '',
          lastName: prefill.lastName || '',
          phoneNumber: prefill.phoneNumber || ''
        });

        if (prefill.productIds && prefill.productIds.length > 0) {
          this.form.patchValue({
            productAccessType: 'LIMITED_PRODUCTS',
            productIds: prefill.productIds
          });
        }
      }
    }

    // Handle disabling for request-access
    if (this.from === 'request-access') {
      this.form.get('firstName')?.disable();
      this.form.get('lastName')?.disable();
      this.form.get('email')?.disable();
      this.form.get('phoneNumber')?.disable();
    }

    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.formSubmitted.set(false);
    this.form.get('email')?.enable();
    this.form.reset({
      status: 'ACTIVE',
      isTwoFAEnabled: true,
      productAccessType: 'ALL_PRODUCTS',
      productIds: [],
      note: '',
    });
    this.closed.emit();
  }

  submitForm() {
    this.formSubmitted.set(true);

    // Trim all string fields before validation
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (typeof control?.value === 'string') {
        control.setValue(control.value.trim(), { emitEvent: false });
      }
    });

    if (this.form.invalid) {
      this.scrollToFirstInvalidControl();
      return;
    }

    this.isLoading.set(true);

    const formValue = this.form.getRawValue();

    // Sanitize: Convert empty strings to null for backend compatibility
    const sanitizedValue: any = {};
    Object.keys(formValue).forEach((key) => {
      const value = formValue[key];
      sanitizedValue[key] = typeof value === 'string' && value.trim() === '' ? null : value;
    });

    let payload: any = {
      ...sanitizedValue,
      status: sanitizedValue.status?.toUpperCase() || null,
    };

    if (this.isEditing()) {
      payload = {
        firstName: sanitizedValue.firstName,
        lastName: sanitizedValue.lastName,
        phoneNumber: sanitizedValue.phoneNumber,
        roleId: sanitizedValue.roleId,
        status: sanitizedValue.status?.toUpperCase() || null,
        isTwoFAEnabled: sanitizedValue.isTwoFAEnabled,
        productAccessType: sanitizedValue.productAccessType,
        productIds:
          sanitizedValue.productAccessType === 'ALL_PRODUCTS'
            ? this.allProducts().map((p) => p.id)
            : this.allProducts()
                .filter(
                  (p) =>
                    sanitizedValue.productIds.includes(p.id) ||
                    (p.pid && sanitizedValue.productIds.includes(p.pid)),
                )
                .map((p) => p.id),
        productPids:
          sanitizedValue.productAccessType === 'ALL_PRODUCTS'
            ? this.allProducts().map((p) => p.pid)
            : this.allProducts()
                .filter(
                  (p) =>
                    sanitizedValue.productIds.includes(p.id) ||
                    (p.pid && sanitizedValue.productIds.includes(p.pid)),
                )
                .map((p) => p.pid),
      };
    } else {
      payload.productIds =
        sanitizedValue.productAccessType === 'ALL_PRODUCTS'
          ? this.allProducts().map((p) => p.id)
          : this.allProducts()
              .filter(
                (p) =>
                  sanitizedValue.productIds.includes(p.id) ||
                  (p.pid && sanitizedValue.productIds.includes(p.pid)),
              )
              .map((p) => p.id);

      payload.productPids =
        sanitizedValue.productAccessType === 'ALL_PRODUCTS'
          ? this.allProducts().map((p) => p.pid)
          : this.allProducts()
              .filter(
                (p) =>
                  sanitizedValue.productIds.includes(p.id) ||
                  (p.pid && sanitizedValue.productIds.includes(p.pid)),
              )
              .map((p) => p.pid);

      if (this.isRequest()) {
        payload.isRequest = true;
      }
    }

    const endpoint = this.isEditing() ? `/user/${this.editingId()}` : '/user';
    const request = this.isEditing()
      ? this.apiservice.put(endpoint, payload)
      : this.apiservice.post(endpoint, payload);

    request.subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        if (response?.message) {
          this.toasterService.success(
            response?.message ||
              `User ${this.isEditing() ? 'updated' : 'saved'} successfully`,
            'Success',
          );
          this.closePanel();
          this.saved.emit();
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        // this.toasterService.error(
        //   err?.message ||
        //     `Error ${this.isEditing() ? 'updating' : 'saving'} user`,
        //   'Error',
        // );
      },
    });
  }

  onInputChange(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      control.markAsUntouched();
      control.markAsPristine();
    }
  }

  preventLeadingSpace(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (event.key === ' ' && target.selectionStart === 0) {
      event.preventDefault();
    }
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (!control || (!control.touched && !this.formSubmitted()) || control.valid) return undefined;

    if (control.hasError('required')) return 'This field is required';
    if (control.hasError('email')) return 'Please enter a valid email address';
    if (control.hasError('minlength')) {
      const actual = control.errors?.['minlength']?.actualLength;
      const required = control.errors?.['minlength']?.requiredLength;
      return `Minimum ${required} characters required (current: ${actual})`;
    }
    if (control.hasError('maxlength')) {
      const actual = control.errors?.['maxlength']?.actualLength;
      const required = control.errors?.['maxlength']?.requiredLength;
      return `Maximum ${required} characters allowed (current: ${actual})`;
    }
    if (control.hasError('pattern')) {
      if (controlName === 'firstName' || controlName === 'lastName') return 'Alphabetic characters only';
      if (controlName === 'phoneNumber') return 'Digits only';
      if (controlName === 'note') return 'Only letters, numbers, periods, and commas allowed';
      return 'Invalid format';
    }

    return undefined;
  }
  private scrollToFirstInvalidControl() {
    const uiOrder = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'roleId',
      'status',
      'productAccessType',
      'productIds',
      'note',
    ];

    const firstInvalidControl = uiOrder.find((field) => {
      const control = this.form.get(field);
      return control?.invalid;
    });

    if (firstInvalidControl) {
      const invalidControlElement: HTMLElement | null = document.querySelector(
        `[formcontrolname="${firstInvalidControl}"]`,
      );

      if (invalidControlElement) {
        invalidControlElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Focus the input inside the app-input component
        const input = invalidControlElement.querySelector('input, select, textarea') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    }
  }
}
